import { Icon, IconPack } from '@healingpaper/cell'
import Link from 'next/link'
import styled from 'styled-components'

import CheetahIcon, { CheetahIconPack } from '@/components/atomic/atoms/CheetahIcon'
import { generateTagId } from '@/utils/test-automation'
import { useLocales } from '@/shared/hooks/useLocales'
import { isProd } from '@/shared/utils'

export type GlobalNavBarTabKey = 'home' | 'hospital' | 'event' | 'community' | 'column' | 'unni-guide'
interface Props {
  selectedGnbTabKey?: GlobalNavBarTabKey
  text: {
    home: string
    homeLink: string
    hospital: string
    hospitalLink: string
    event: string
    eventLink: string
    column: string
    columnLink: string
    community: string
    communityLink: string
  }
  onclick?: (key: GlobalNavBarTabKey) => void
}
const GlobalNavBar = ({ selectedGnbTabKey, text, onclick }: Props): JSX.Element => {
  const { isLocale } = useLocales()

  const isPostTabVisible = !isLocale.US && !isLocale.TH
  const isUnniGuideTabVisible = false

  return (
    <StyledContainer>
      <StyledNav>
        <StyledTabList>
          <StyledTab>
            <Link href="/" id={generateTagId('all', 'tab', 'link', 'moveTo', 'home')}>
              <StyledText $selected={selectedGnbTabKey === 'home'} onClick={() => onclick?.('home')}>
                {selectedGnbTabKey === 'home' ? (
                  <Icon name={IconPack.NAVBAR_HOME_ON} width={24} height={24} />
                ) : (
                  <Icon name={IconPack.NAVBAR_HOME_OFF} width={24} height={24} />
                )}
                {text.home}
              </StyledText>
            </Link>
          </StyledTab>
          <StyledTab>
            <Link href="/hospitals" id={generateTagId('all', 'tab', 'link', 'moveTo', 'hospital')}>
              <StyledText $selected={selectedGnbTabKey === 'hospital'} onClick={() => onclick?.('hospital')}>
                {selectedGnbTabKey === 'hospital' ? (
                  <Icon name={IconPack.NAVBAR_HOSPITAL_ON} width={24} height={24} />
                ) : (
                  <Icon name={IconPack.NAVBAR_HOSPITAL_OFF} width={24} height={24} />
                )}
                {text.hospital}
              </StyledText>
            </Link>
          </StyledTab>
          <StyledTab>
            <Link href="/events" id={generateTagId('all', 'tab', 'link', 'moveTo', 'event')}>
              <StyledText $selected={selectedGnbTabKey === 'event'} onClick={() => onclick?.('event')}>
                {selectedGnbTabKey === 'event' ? (
                  <Icon name={IconPack.NAVBAR_EVENT_ON} width={24} height={24} />
                ) : (
                  <Icon name={IconPack.NAVBAR_EVENT_OFF} width={24} height={24} />
                )}
                {text.event}
              </StyledText>
            </Link>
          </StyledTab>
          <StyledTab>
            <Link href="/community" id={generateTagId('all', 'tab', 'link', 'moveTo', 'community')}>
              <StyledText $selected={selectedGnbTabKey === 'community'} onClick={() => onclick?.('community')}>
                {selectedGnbTabKey === 'community' ? (
                  <img src="/assets/icon/navbarCommunityOn.svg" alt="community icon" />
                ) : (
                  <img src="/assets/icon/navbarCommunityOff.svg" alt="community icon" />
                )}
                {text.community}
              </StyledText>
            </Link>
          </StyledTab>
          {isPostTabVisible && (
            <StyledTab>
              <Link href="/posts" id={generateTagId('all', 'tab', 'link', 'moveTo', 'column')}>
                <StyledText $selected={selectedGnbTabKey === 'column'} onClick={() => onclick?.('column')}>
                  {selectedGnbTabKey === 'column' ? (
                    <CheetahIcon name={CheetahIconPack.NAVBAR_COLUMN_ON} width={24} height={24} />
                  ) : (
                    <CheetahIcon name={CheetahIconPack.NAVBAR_COLUMN_OFF} width={24} height={24} />
                  )}
                  {text.column}
                </StyledText>
              </Link>
            </StyledTab>
          )}

          {isUnniGuideTabVisible && (
            <StyledTab>
              <Link href="/unni-guide" id={generateTagId('all', 'tab', 'link', 'moveTo', 'unni-guide')}>
                <StyledText $selected={selectedGnbTabKey === 'unni-guide'} onClick={() => onclick?.('unni-guide')}>
                  {selectedGnbTabKey === 'unni-guide' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 7.8C3 6.11984 3 5.27976 3.32698 4.63803C3.6146 4.07354 4.07354 3.6146 4.63803 3.32698C5.27976 3 6.11984 3 7.8 3H16.2C17.8802 3 18.7202 3 19.362 3.32698C19.9265 3.6146 20.3854 4.07354 20.673 4.63803C21 5.27976 21 6.11984 21 7.8V13.2C21 14.8802 21 15.7202 20.673 16.362C20.3854 16.9265 19.9265 17.3854 19.362 17.673C18.7202 18 17.8802 18 16.2 18H13.6837C13.0597 18 12.7477 18 12.4492 18.0613C12.1844 18.1156 11.9282 18.2055 11.6875 18.3285C11.4162 18.4671 11.1725 18.662 10.6852 19.0518L8.29976 20.9602C7.88367 21.2931 7.67563 21.4595 7.50054 21.4597C7.34827 21.4599 7.20422 21.3906 7.10923 21.2716C7 21.1348 7 20.8684 7 20.3355V18C6.07003 18 5.60504 18 5.22354 17.8978C4.18827 17.6204 3.37962 16.8117 3.10222 15.7765C3 15.395 3 14.93 3 14V7.8Z"
                        fill="#FF540F"
                        stroke="#FF540F"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.5 12.5C8.5 12.5 9.8125 14 12 14C14.1875 14 15.5 12.5 15.5 12.5M14.75 7.5H14.76M9.25 7.5H9.26M15.25 7.5C15.25 7.77614 15.0261 8 14.75 8C14.4739 8 14.25 7.77614 14.25 7.5C14.25 7.22386 14.4739 7 14.75 7C15.0261 7 15.25 7.22386 15.25 7.5ZM9.75 7.5C9.75 7.77614 9.52614 8 9.25 8C8.97386 8 8.75 7.77614 8.75 7.5C8.75 7.22386 8.97386 7 9.25 7C9.52614 7 9.75 7.22386 9.75 7.5Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M8.5 12.5C8.5 12.5 9.8125 14 12 14C14.1875 14 15.5 12.5 15.5 12.5M14.75 7.5H14.76M9.25 7.5H9.26M7 18V20.3355C7 20.8684 7 21.1348 7.10923 21.2716C7.20422 21.3906 7.34827 21.4599 7.50054 21.4597C7.67563 21.4595 7.88367 21.2931 8.29976 20.9602L10.6852 19.0518C11.1725 18.662 11.4162 18.4671 11.6875 18.3285C11.9282 18.2055 12.1844 18.1156 12.4492 18.0613C12.7477 18 13.0597 18 13.6837 18H16.2C17.8802 18 18.7202 18 19.362 17.673C19.9265 17.3854 20.3854 16.9265 20.673 16.362C21 15.7202 21 14.8802 21 13.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V14C3 14.93 3 15.395 3.10222 15.7765C3.37962 16.8117 4.18827 17.6204 5.22354 17.8978C5.60504 18 6.07003 18 7 18ZM15.25 7.5C15.25 7.77614 15.0261 8 14.75 8C14.4739 8 14.25 7.77614 14.25 7.5C14.25 7.22386 14.4739 7 14.75 7C15.0261 7 15.25 7.22386 15.25 7.5ZM9.75 7.5C9.75 7.77614 9.52614 8 9.25 8C8.97386 8 8.75 7.77614 8.75 7.5C8.75 7.22386 8.97386 7 9.25 7C9.52614 7 9.75 7.22386 9.75 7.5Z"
                        stroke="#4C4C4C"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  UnniGuide
                </StyledText>
              </Link>
            </StyledTab>
          )}
        </StyledTabList>
      </StyledNav>
    </StyledContainer>
  )
}
const StyledContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  height: 65px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.gray400};
`

const StyledNav = styled.nav`
  height: 100%;
`

const StyledTabList = styled.ul`
  list-style: none;
  height: 100%;
  padding: 0px;
  margin: 0px;
  display: flex;
`

const StyledTab = styled.li`
  flex: 1;
  cursor: pointer;
  a {
    text-decoration: none;
  }
`

const StyledText = styled.span<{ $selected: boolean }>`
  color: ${({ theme, $selected }) => ($selected ? theme.colors.orange : theme.colors.gray800)};
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.typography.caption1};
  svg,
  img {
    margin-bottom: 4px;
  }
`

export default GlobalNavBar
